import apiClient from './apiClient';
import { logger } from '../utils/logger'
import {
  Invoice, 
  InvoiceFilters, 
  InvoiceStats, 
  InvoiceApiResponse,
  CreateInvoiceRequest 
} from '../types/financial.types';

class InvoiceService {
  private baseURL = '/invoices';

  /**
   * Obter lista de faturas com filtros
   */
  async getInvoices(filters: InvoiceFilters = {}): Promise<{
    invoices: Invoice[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.payment_method && filters.payment_method !== 'all') {
        params.append('payment_method', filters.payment_method);
      }
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }

      const response = await apiClient.get<InvoiceApiResponse<{
        invoices: Invoice[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          total_pages: number;
        };
      }>>(`${this.baseURL}?${params.toString()}`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao obter faturas');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter faturas:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter faturas');
    }
  }

  /**
   * Obter fatura por ID
   */
  async getInvoiceById(id: string): Promise<Invoice> {
    try {
      const response = await apiClient.get<InvoiceApiResponse<Invoice>>(`${this.baseURL}/${id}`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao obter fatura');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter fatura:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter fatura');
    }
  }

  /**
   * Criar nova fatura
   */
  async createInvoice(invoiceData: CreateInvoiceRequest): Promise<Invoice> {
    try {
      const response = await apiClient.post<InvoiceApiResponse<Invoice>>(
        this.baseURL, 
        invoiceData
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao criar fatura');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao criar fatura:', error);
      throw new Error(error.response?.data?.error || 'Erro ao criar fatura');
    }
  }

  /**
   * Atualizar fatura
   */
  async updateInvoice(id: string, invoiceData: Partial<CreateInvoiceRequest>): Promise<Invoice> {
    try {
      const response = await apiClient.put<InvoiceApiResponse<Invoice>>(
        `${this.baseURL}/${id}`, 
        invoiceData
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao atualizar fatura');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao atualizar fatura:', error);
      throw new Error(error.response?.data?.error || 'Erro ao atualizar fatura');
    }
  }

  /**
   * Cancelar fatura
   */
  async cancelInvoice(id: string): Promise<Invoice> {
    try {
      const response = await apiClient.post<InvoiceApiResponse<Invoice>>(
        `${this.baseURL}/${id}/cancel`
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao cancelar fatura');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao cancelar fatura:', error);
      throw new Error(error.response?.data?.error || 'Erro ao cancelar fatura');
    }
  }

  /**
   * Obter estatísticas de faturas
   */
  async getInvoiceStats(): Promise<InvoiceStats> {
    try {
      const response = await apiClient.get<InvoiceApiResponse<InvoiceStats>>(
        `${this.baseURL}/stats`
      );
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao obter estatísticas');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter estatísticas de faturas:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter estatísticas');
    }
  }

  /**
   * Obter link de pagamento da fatura
   */
  async getPaymentLink(id: string): Promise<{ payment_link: string; qr_code?: string; barcode?: string }> {
    try {
      const response = await apiClient.get<InvoiceApiResponse<{
        payment_link: string;
        qr_code?: string;
        barcode?: string;
      }>>(`${this.baseURL}/${id}/payment-link`);
      
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || 'Erro ao obter link de pagamento');
      }

      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter link de pagamento:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter link de pagamento');
    }
  }

  /**
   * Obter estatísticas de faturas
   */
  async getStats(): Promise<InvoiceStats> {
    try {
      const response = await apiClient.get(`${this.baseURL}/stats`);
      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter estatísticas:', error);
      throw new Error(error.response?.data?.error || 'Erro ao obter estatísticas');
    }
  }

  /**
   * Exportar faturas para CSV
   */
  async exportInvoices(filters: InvoiceFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.payment_method && filters.payment_method !== 'all') {
        params.append('payment_method', filters.payment_method);
      }
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await apiClient.get(`${this.baseURL}/export?${params.toString()}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error: any) {
      logger.error('Erro ao exportar faturas:', error);
      throw new Error(error.response?.data?.error || 'Erro ao exportar faturas');
    }
  }
}

export const invoiceService = new InvoiceService();
export default invoiceService;




